import { useEffect, useRef } from "react";

// Dialogs currently on screen, innermost last. Only the top one answers Back, so a check
// sheet opened from the PM status form closes on its own rather than taking the form too.
const openDialogs = [];
let dialogCounter = 0;

// ONE history entry covers all open dialogs, rather than one entry each. Per-dialog
// entries broke closing a dialog to open another in the same click — Delete Unit closes
// the edit form and opens a confirmation, and the edit form's asynchronous history.back()
// landed after the confirmation had pushed its own entry, popping that instead and closing
// the confirmation on the spot. The unit could then never be deleted.
let markerPushed = false;

const soon = fn => (typeof queueMicrotask === "function" ? queueMicrotask(fn) : Promise.resolve().then(fn));

function armMarker() {
  if (markerPushed) return;
  try {
    window.history.pushState({ dialog: true }, "");
    markerPushed = true;
  } catch { /* history unavailable — dialogs still work, Back just won't close them */ }
}

function disarmMarkerIfIdle() {
  // Deferred by a microtask: a dialog handing over to another unmounts and mounts within
  // one commit, and checking immediately would tear down the entry the new dialog just
  // took over.
  soon(() => {
    if (openDialogs.length || !markerPushed) return;
    markerPushed = false;
    if (window.history.state && window.history.state.dialog) {
      try { window.history.back(); } catch { /* ignore */ }
    }
  });
}

/**
 * Makes the browser / device Back button dismiss a dialog instead of navigating the page
 * out from under it — Back used to change the view, unmounting the whole screen and
 * throwing away anything half-typed into the form.
 *
 * Closing with a button removes the entry again, so the next Back press is not spent on a
 * dialog that has already gone.
 */
export function useBackToClose(onClose) {
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const id = ++dialogCounter;
    openDialogs.push(id);
    armMarker();

    function onPop() {
      if (openDialogs[openDialogs.length - 1] !== id) return;   // not the top dialog
      const hasDialogBelow = openDialogs.length > 1;
      markerPushed = false;      // the browser has already taken the entry
      if (typeof closeRef.current === "function") closeRef.current();
      // Something is still open underneath — put the marker back so the next Back closes it.
      if (hasDialogBelow) soon(armMarker);
    }
    window.addEventListener("popstate", onPop);

    return () => {
      window.removeEventListener("popstate", onPop);
      const i = openDialogs.indexOf(id);
      if (i > -1) openDialogs.splice(i, 1);
      disarmMarkerIfIdle();
    };
  }, []);
}
